// Kiểu thông báo của mốc donate (sếp chốt 04/11): Thường, Nổi bật, Đặc biệt. Mỗi kiểu là một bộ cài sẵn;
// streamer bật/tắt từng phần (nhạc mở màn, huy hiệu, hiệu ứng màn hình) và đổi giọng đọc, mức to hơn thì cố định theo kiểu.
// Hàm thuần dùng chung cho overlay OBS và trang cài đặt.

import { DEFAULT_TEMPLATE, formatCa, renderTemplate } from './donate-alert';
import { normalizeVolume } from './overlay-volume';

export type AlertKind = 'normal' | 'highlight' | 'special';
export type MedalTone = 'silver' | 'gold';
export type ScreenEffect = 'glow' | 'fireworks';
export type IntroStyle = 'short' | 'long';

export const ALERT_KINDS: AlertKind[] = ['normal', 'highlight', 'special'];

export const KIND_LABELS: Record<AlertKind, string> = {
  normal: 'Thường',
  highlight: 'Nổi bật',
  special: 'Đặc biệt',
};

/** Giọng Wavenet tiếng Việt mà máy chủ cho phép (giới tính theo danh mục giọng của Google: A nữ, B nam, C nữ, D nam). */
export const TTS_VOICES: { id: string; label: string }[] = [
  { id: 'vi-VN-Wavenet-A', label: 'Giọng nữ 1' },
  { id: 'vi-VN-Wavenet-C', label: 'Giọng nữ 2' },
  { id: 'vi-VN-Wavenet-B', label: 'Giọng nam 1' },
  { id: 'vi-VN-Wavenet-D', label: 'Giọng nam 2' },
];
export const DEFAULT_TTS_VOICE = 'vi-VN-Wavenet-A';

export const isAllowedVoice = (v: unknown): v is string => typeof v === 'string' && TTS_VOICES.some((x) => x.id === v);

export interface KindPreset {
  /** Số điểm âm lượng cộng thêm trên mức streamer đặt. */
  boost: number;
  intro: IntroStyle | null;
  medal: MedalTone | null;
  effect: ScreenEffect | null;
  voice: string;
  rate: number;
  /** Đọc có ngắt nghỉ giữa tên, số Ca và lời nhắn (chỉ thêm dấu câu, không thêm chữ). */
  pauses: boolean;
  extraMs: number;
}

export const KIND_PRESETS: Record<AlertKind, KindPreset> = {
  normal: { boost: 0, intro: null, medal: null, effect: null, voice: DEFAULT_TTS_VOICE, rate: 1, pauses: false, extraMs: 0 },
  highlight: {
    boost: 10,
    intro: 'short',
    medal: 'silver',
    effect: 'glow',
    voice: 'vi-VN-Wavenet-C',
    rate: 0.95,
    pauses: false,
    extraMs: 3000,
  },
  special: {
    boost: 20,
    intro: 'long',
    medal: 'gold',
    effect: 'fireworks',
    voice: 'vi-VN-Wavenet-D',
    rate: 0.9,
    pauses: true,
    extraMs: 6000,
  },
};

/** Thời lượng nhạc mở màn dựng sẵn. */
export const INTRO_MS: Record<IntroStyle, number> = { short: 2000, long: 4000 };

export const normalizeKind = (raw: unknown): AlertKind =>
  raw === 'highlight' || raw === 'special' ? raw : 'normal';

/** Các trường kiểu trên một mốc (khớp phản hồi máy chủ; thiếu trường nào thì theo kiểu). */
export interface KindFields {
  kind?: string | null;
  introEnabled?: boolean | null;
  medalEnabled?: boolean | null;
  effectEnabled?: boolean | null;
  voice?: string | null;
  introUrl?: string | null;
  medalUrl?: string | null;
}

export interface ResolvedKind {
  kind: AlertKind;
  boost: number;
  intro: { style: IntroStyle; url: string | null } | null;
  medal: { tone: MedalTone; url: string | null } | null;
  effect: ScreenEffect | null;
  voice: string;
  rate: number;
  pauses: boolean;
  extraMs: number;
}

// Thường mà streamer bật thêm một phần thì dùng phiên bản của Nổi bật cho phần đó.
const FALLBACK_PART = KIND_PRESETS.highlight;

/** Bộ cài sẵn của kiểu, đè bằng lựa chọn riêng của mốc (null/thiếu = theo kiểu). */
export const resolveAlertKind = (fields?: KindFields | null): ResolvedKind => {
  const kind = normalizeKind(fields?.kind);
  const p = KIND_PRESETS[kind];
  const pick = (override: boolean | null | undefined, preset: boolean) =>
    typeof override === 'boolean' ? override : preset;
  const introOn = pick(fields?.introEnabled, !!p.intro);
  const medalOn = pick(fields?.medalEnabled, !!p.medal);
  const effectOn = pick(fields?.effectEnabled, !!p.effect);
  return {
    kind,
    boost: p.boost,
    intro: introOn ? { style: p.intro || (FALLBACK_PART.intro as IntroStyle), url: fields?.introUrl || null } : null,
    medal: medalOn ? { tone: p.medal || (FALLBACK_PART.medal as MedalTone), url: fields?.medalUrl || null } : null,
    effect: effectOn ? p.effect || FALLBACK_PART.effect : null,
    voice: isAllowedVoice(fields?.voice) ? (fields?.voice as string) : p.voice,
    rate: p.rate,
    pauses: p.pauses,
    extraMs: p.extraMs,
  };
};

/** Giá trị ghi đè cần lưu: trùng với bộ cài sẵn thì null (để sau này đổi bộ cài sẵn vẫn theo). */
export const overrideFor = (value: boolean, presetOn: boolean): boolean | null => (value === presetOn ? null : value);

/** Kiểu cài sẵn theo số Ca: mốc cao nhất Đặc biệt, mốc kế tiếp Nổi bật, còn lại Thường. */
export const presetKindFor = (amounts: number[], amount: number): AlertKind => {
  const desc = Array.from(new Set(amounts.map((a) => Number(a) || 0))).sort((a, b) => b - a);
  const rank = desc.indexOf(Number(amount) || 0);
  if (rank === 0) return 'special';
  if (rank === 1) return 'highlight';
  return 'normal';
};

/** Áp kiểu cài sẵn cho cả danh sách mốc, bỏ mọi lựa chọn riêng. */
export const applyPresetKinds = <T extends { minAmount: number } & KindFields>(tiers: T[]): T[] => {
  const amounts = tiers.map((t) => Number(t.minAmount) || 0);
  return tiers.map((t) => ({
    ...t,
    kind: presetKindFor(amounts, t.minAmount),
    introEnabled: null,
    medalEnabled: null,
    effectEnabled: null,
    voice: null,
  }));
};

/** Âm lượng sau khi cộng mức to hơn của kiểu (0–120). Streamer đã tắt hẳn (0) thì giữ 0: kiểu nào cũng không bật lại tiếng. */
export const boostedVolume = (volume: unknown, boost: number): number => {
  const base = normalizeVolume(volume);
  if (base === 0) return 0;
  return base + Math.min(20, Math.max(0, Math.round(Number(boost) || 0)));
};

/** Hệ số khuếch đại cho Web Audio; có thể lớn hơn 1 (ví dụ 100 + 20 = 1,2). */
export const kindGain = (volume: unknown, boost: number): number => boostedVolume(volume, boost) / 100;

// ── Câu đọc ──

// Dấu ngắt tạm dùng ký tự vùng riêng (không bao giờ có trong tên hay lời nhắn thật).
const SHORT_PAUSE = '\uE000';
const LONG_PAUSE = '\uE001';
const PUNCT = /[!?.,:;…]/;

// Đổi dấu ngắt tạm thành dấu câu thật. Đầu/cuối câu bỏ hẳn; sát dấu câu có sẵn thì nhường dấu có sẵn.
const settlePauses = (s: string): string =>
  s
    .replace(/\s*([\uE000\uE001]+)\s*/g, (m: string, run: string, offset: number, whole: string) => {
      const prev = whole[offset - 1];
      const next = whole[offset + m.length];
      if (prev === undefined || next === undefined) return '';
      if (PUNCT.test(next)) return '';
      if (PUNCT.test(prev)) return ' ';
      return run.includes(LONG_PAUSE) ? '... ' : ', ';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();

/**
 * Câu máy đọc. Không ngắt nghỉ thì giống hệt trước đây: câu hiển thị, thêm lời nhắn nếu không có ghi âm.
 * Ngắt nghỉ (Đặc biệt): cùng những chữ đó, chỉ chèn dấu phẩy sau tên và dấu ba chấm sau số Ca và trước lời nhắn.
 */
export const spokenAlertText = (o: {
  template: string | null;
  ten: string;
  soTien: number;
  loiNhan?: string | null;
  message?: string;
  hasVoice: boolean;
  pauses: boolean;
}): string => {
  const ten = o.ten ?? '';
  const soTien = formatCa(o.soTien);
  const loiNhan = o.loiNhan ?? '';
  const render = (map: Record<string, string>) =>
    (o.template || DEFAULT_TEMPLATE).replace(/\{(ten|so_tien|loi_nhan)\}/g, (_m, key: string) => map[key]).trim();
  if (!o.pauses) {
    const text = renderTemplate(o.template, { ten: o.ten, soTien: o.soTien, loiNhan: o.loiNhan });
    if (o.hasVoice) return text;
    return o.message ? `${text}. ${o.message}` : text;
  }
  let text = render({
    ten: `${ten}${SHORT_PAUSE}`,
    so_tien: `${soTien}${LONG_PAUSE}`,
    loi_nhan: loiNhan ? `${LONG_PAUSE}${loiNhan}` : '',
  });
  if (!o.hasVoice && o.message) text = `${text}${LONG_PAUSE}${o.message}`;
  return settlePauses(text);
};
